import { useEffect, useState } from "react";
import { Plus, Trash2, Leaf, Sparkles, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RubricEditor, RubricSummary } from "@/components/rubric/RubricParts";

export const DEFAULT_STAGE = (kind) => ({
  name: "", rubric: kind === "stop"
    ? { type: "self", unit: "", counts_from: "showed_up", tiers: [{ key: "full", label: "Full", rule: {}, weight: 1 }, { key: "partial", label: "Partial", rule: {}, weight: 0.6 }, { key: "small", label: "Small step", rule: {}, weight: 0.3 }, { key: "showed_up", label: "Noticed", rule: {}, weight: 0.1 }] }
    : { type: "yesno", unit: "", counts_from: "showed_up", tiers: [{ key: "full", label: "Done", rule: {}, weight: 1 }] },
});

function StageCard({ stage, i, state, templates, kind, onChange, onRemove, bottomLabels }) {
  const applyTemplate = (key) => {
    const t = templates.find((x) => x.key === key);
    if (t) onChange({ ...stage, name: stage.name || t.name, rubric: JSON.parse(JSON.stringify(t.rubric)) });
  };
  const bottom = stage.rubric.tiers[stage.rubric.tiers.length - 1];
  const tone = state === "current" ? "border-amber-400 bg-amber-50/40" : state === "past" ? "border-emerald-200 bg-emerald-50/30" : "border-stone-200 opacity-80";
  return (
    <div className={`rounded-2xl border p-4 space-y-3 ${tone}`} data-testid={`stage-card-${i}`}>
      <div className="flex items-center gap-2">
        {state === "past" && <Leaf className="w-4 h-4 text-emerald-700" />}
        <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Stage {i + 1}{state === "current" ? " · current" : ""}</span>
        <input value={stage.name} onChange={(e) => onChange({ ...stage, name: e.target.value })} placeholder="Name this stage (e.g. Lower potency)" maxLength={60}
          data-testid={`stage-name-${i}`} className="flex-1 px-3 py-1.5 rounded-lg border border-stone-300 bg-white text-sm focus:border-amber-600 outline-none" />
        {onRemove && <button onClick={onRemove} aria-label="Remove stage" data-testid={`stage-remove-${i}`} className="p-1.5 rounded-lg text-stone-400 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>}
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-stone-500">Start from a template:</span>
        <select onChange={(e) => applyTemplate(e.target.value)} defaultValue="" data-testid={`stage-template-${i}`} className="px-2 py-1 rounded-lg border border-stone-300 bg-white">
          <option value="">— pick one —</option>
          {templates.filter((t) => t.kind === "any" || t.kind === kind).map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
        </select>
      </div>
      <RubricEditor value={stage.rubric} onChange={(rubric) => onChange({ ...stage, rubric })} idPrefix={`stage-${i}-rubric`} />
      {bottom && bottom.key === "showed_up" && kind === "stop" && (
        <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-stone-600" data-testid={`stage-bottom-labels-${i}`}>
          <span>Bottom tier is awareness, not attendance. Call it:</span>
          {bottomLabels.map((l) => <button key={l} type="button" onClick={() => onChange({ ...stage, rubric: { ...stage.rubric, tiers: stage.rubric.tiers.map((t) => t.key === "showed_up" ? { ...t, label: l } : t) } })}
            className={`px-2 py-0.5 rounded-full border ${bottom.label === l ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 hover:border-amber-500"}`}>{l}</button>)}
        </div>
      )}
      <div className="text-[11px] text-stone-500">Check-in buttons will read: {stage.rubric.tiers.map((t) => t.label).join(" · ")}</div>
    </div>
  );
}

/** Vertical ladder editor. `onSave(stages, index)`; used by the client and (as a draft) by the clinician. */
export function StageBuilder({ initial, index = 0, kind = "stop", onSave, onCancel, saveLabel = "Save stages", busy, inline = false, onChangeStages, title = "", category = "", allowAi = true }) {
  const [stages, setStagesRaw] = useState(initial?.length ? initial : [DEFAULT_STAGE(kind)]);
  const setStages = (next) => { setStagesRaw(next); onChangeStages?.(next); };
  useEffect(() => { if (inline) onChangeStages?.(stages); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [meta, setMeta] = useState({ templates: [], bottom_tier_labels: ["Noticed"], max_stages: 6, ai_draft: "off" });
  useEffect(() => { api.get("/habits/stage-templates").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null); // { prev, note }
  const update = (i, s) => setStages(stages.map((x, j) => (j === i ? s : x)));
  const valid = stages.every((s) => s.name.trim() && s.rubric.tiers.length);
  const suggest = async () => {
    if (!title.trim()) { toast("Give the habit a name first, then I can suggest a plan."); return; }
    setDrafting(true);
    try {
      const r = await api.post("/habits/stages/suggest", { title, kind, category });
      setDraft({ prev: stages, note: r.data.note });
      setStages(r.data.stages);
    } catch (e) { toast(e?.response?.data?.detail || "Couldn't draft right now — you can still build it by hand."); }
    finally { setDrafting(false); }
  };
  const discard = () => { setStages(draft.prev); setDraft(null); };
  return (
    <div className="space-y-3" data-testid="stage-builder">
      {allowAi && meta.ai_draft === "on" && (
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" onClick={suggest} disabled={drafting} data-testid="stage-suggest-plan"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-60 transition-colors">
            <Sparkles className="w-3.5 h-3.5" /> {drafting ? "Drafting…" : "Suggest a plan"}
          </button>
          {draft && <button type="button" onClick={discard} data-testid="stage-draft-discard" className="inline-flex items-center gap-1 text-xs font-semibold text-stone-600 hover:text-stone-900"><Undo2 className="w-3.5 h-3.5" /> Discard draft</button>}
          {draft?.note && <span className="text-xs text-stone-600" data-testid="stage-draft-note">{draft.note}</span>}
          {draft && !draft.note && <span className="text-xs text-stone-500" data-testid="stage-draft-ai-note">A starting point — edit anything, nothing is saved until you tap Save.</span>}
        </div>
      )}
      {allowAi && meta.ai_draft === "linked" && (
        <p className="text-xs text-stone-500" data-testid="stage-ai-linked-note">Your coach can draft stages for you; AI drafting for linked people you support arrives once our HIPAA AI lane is live.</p>
      )}
      {stages.map((s, i) => (
        <StageCard key={i} stage={s} i={i} kind={kind} templates={meta.templates} bottomLabels={meta.bottom_tier_labels}
          state={i < index ? "past" : i === index ? "current" : "future"} onChange={(x) => update(i, x)} onRemove={stages.length > 1 ? () => setStages(stages.filter((_, j) => j !== i)) : null} />
      ))}
      <div className="flex gap-2 flex-wrap">
        {stages.length < meta.max_stages && <button type="button" onClick={() => setStages([...stages, DEFAULT_STAGE(kind)])} data-testid="stage-add" className="text-xs font-semibold px-3 py-1.5 rounded-full border border-stone-300 bg-white inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add a stage</button>}
        {!inline && <button type="button" onClick={() => onSave(stages.map((s) => ({ ...s, name: s.name.trim() })), index)} disabled={!valid || busy} data-testid="stage-save" className="amber-btn text-xs py-1.5 disabled:opacity-40">{busy ? "Saving…" : saveLabel}</button>}
        {onCancel && <button type="button" onClick={onCancel} data-testid="stage-cancel" className="text-xs font-semibold px-3 py-1.5 rounded-full text-stone-600 hover:text-stone-900">Cancel</button>}
      </div>
    </div>
  );
}

/** Compact read-only ladder (client card header, clinician detail). */
export function StageLadder({ stage, testid = "stage-ladder" }) {
  if (!stage) return null;
  return (
    <ol className="flex flex-wrap gap-1.5" data-testid={testid}>
      {stage.names.map((n, i) => (
        <li key={i} className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${i < stage.index ? "bg-emerald-50 text-emerald-800" : i === stage.index ? "bg-amber-100 text-amber-900 font-semibold ring-1 ring-amber-300" : "bg-stone-100 text-stone-500"}`}>
          {i < stage.index && <Leaf className="w-3 h-3" />}{i + 1}. {n}
        </li>
      ))}
    </ol>
  );
}

export function StageProposalCard({ habit, onAnswered }) {
  const p = habit.stage_proposal;
  if (!p) return null;
  const answer = async (accept) => {
    try { await api.post(`/habits/${habit.id}/stages/proposals/${p.id}`, { accept }); toast.success(accept ? "Adopted — you can still change any of it." : "Declined. Your setup stays as it is."); onAnswered(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't answer that"); }
  };
  return (
    <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-3" data-testid={`stage-proposal-${habit.id}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">{p.clinician_name} {p.kind === "move" ? "suggests moving to the next stage" : "drafted stages for this habit"}</div>
      {p.note && <p className="text-sm text-stone-700 mt-1">"{p.note}"</p>}
      {p.kind === "stages" && <ol className="mt-2 space-y-1 text-xs text-stone-700">{p.stages.map((s, i) => <li key={s.id || i}><strong>{i + 1}. {s.name}</strong> — <RubricSummary rubric={s.rubric} /></li>)}</ol>}
      <div className="flex gap-2 mt-3">
        <button data-testid={`stage-proposal-accept-${habit.id}`} onClick={() => answer(true)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-700 text-white">Accept</button>
        <button data-testid={`stage-proposal-decline-${habit.id}`} onClick={() => answer(false)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-stone-300">Not now</button>
      </div>
    </div>
  );
}
